import { Injectable } from '@angular/core';
import { APP_VERSION_STORAGE_KEYS } from '../models/app-version.model';

/**
 * Owns invalidation of *static asset* caches only — the Cache Storage API
 * (service-worker / PWA precaches) and service-worker registrations.
 *
 * Deliberately does NOT touch:
 *  - localStorage/IndexedDB app data (auth tokens, drafts, @ionic/storage)
 *  - any API/session data
 *
 * That is intentionally out of scope here — see SwstoragService for user/
 * session/API data, which this service never reads or writes. Clearing
 * that data (e.g. on logout) is a separate, explicit user action and must
 * never happen implicitly as a side-effect of a routine version bump.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheManagerService {

  /**
   * Cache Storage entry names that must never be deleted by
   * invalidateStaticAssetCaches. This app has no service worker/PWA layer
   * today, so by default every Cache Storage entry is treated as a static
   * asset cache; if a future service worker / PWA toolchain (Workbox,
   * Angular's ngsw, etc.) ever stores API/user data under Cache Storage
   * instead of IndexedDB/localStorage, add its cache name here via
   * setPreservedCacheNames so it's exempt from this blanket deletion.
   */
  private preserveCacheNames: string[] = [];

  private log(message: string, ...rest: any[]) {
    console.log(`[CacheManager] ${message}`, ...rest);
  }

  private warn(message: string, ...rest: any[]) {
    console.warn(`[CacheManager] ${message}`, ...rest);
  }

  setPreservedCacheNames(names: string[]) {
    this.preserveCacheNames = names || [];
  }

  /**
   * Deletes stale static-asset Cache Storage entries and refreshes any
   * service-worker registrations. Call this whenever a build/version
   * change is detected, before reloading the page, so the reload actually
   * picks up new hashed assets instead of re-serving cached ones.
   */
  async invalidateStaticAssetCaches(options: { unregisterServiceWorker?: boolean } = {}): Promise<void> {
    await Promise.all([
      this.clearStaticCacheStorage(),
      this.refreshServiceWorkers(options.unregisterServiceWorker === true)
    ]);
  }

  private async clearStaticCacheStorage(): Promise<void> {
    if (!('caches' in window)) {
      this.log('Cache Storage API not available (native WebView/older browser) — skipping');
      return;
    }
    try {
      const cacheNames = await caches.keys();
      if (!cacheNames.length) {
        this.log('No Cache Storage entries found');
        return;
      }
      const toDelete = cacheNames.filter(name => this.isStaticCacheName(name));
      const skipped = cacheNames.filter(name => !toDelete.includes(name));
      if (skipped.length) {
        this.log('Preserving non-static cache entries', skipped);
      }
      await Promise.all(
        toDelete.map(async name => {
          const removed = await caches.delete(name);
          this.log(`Cache Storage entry ${removed ? 'removed' : 'not found'}: ${name}`);
        })
      );
    } catch (err) {
      this.warn('Failed clearing Cache Storage', err);
    }
  }

  private isStaticCacheName(name: string): boolean {
    return !this.preserveCacheNames.includes(name);
  }

  private async refreshServiceWorkers(unregister: boolean): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      this.log('Service Worker API not available — skipping');
      return;
    }
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (!registrations.length) {
        this.log('No active service-worker registrations');
        return;
      }
      for (const registration of registrations) {
        if (unregister) {
          const result = await registration.unregister();
          this.log(`Service worker unregistered (${result}):`, registration.scope);
        } else {
          await registration.update();
          this.log('Service worker update requested for scope:', registration.scope);
        }
      }
    } catch (err) {
      this.warn('Failed refreshing service worker registrations', err);
    }
  }

  /**
   * Clears application/user/API data (IndexedDB-backed @ionic/storage plus
   * localStorage, minus an explicit preserve-list). This is intentionally
   * SEPARATE from invalidateStaticAssetCaches and is never invoked by the
   * automatic startup/version-check flow — only wire this into an explicit
   * user action (e.g. "Log out", "Reset app data") if/when one is needed.
   */
  async clearApplicationDataCaches(preserveLocalStorageKeys: string[] = []): Promise<void> {
    this.warn('clearApplicationDataCaches invoked — clearing user/session/API data');
    try {
      // Always keep the build-version markers so a data reset doesn't also
      // reset version-change detection back to "first launch".
      const keysToPreserve = [
        APP_VERSION_STORAGE_KEYS.buildVersion,
        APP_VERSION_STORAGE_KEYS.buildTime,
        ...preserveLocalStorageKeys
      ];
      const preserved: Record<string, string> = {};
      keysToPreserve.forEach(key => {
        const value = window.localStorage.getItem(key);
        if (value !== null) {
          preserved[key] = value;
        }
      });
      window.localStorage.clear();
      Object.keys(preserved).forEach(key => window.localStorage.setItem(key, preserved[key]));

      if ('indexedDB' in window && (indexedDB as any).databases) {
        const dbs: any[] = await (indexedDB as any).databases();
        await Promise.all(
          (dbs || [])
            .filter(db => !!db && !!db.name)
            .map(db => new Promise<void>(resolve => {
              const req = indexedDB.deleteDatabase(db.name);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve();
              req.onblocked = () => resolve();
            }))
        );
      }
      this.log('Application data caches cleared');
    } catch (err) {
      this.warn('Failed clearing application data caches', err);
    }
  }
}
